/**
 * @fileoverview `EventDecisionPanel` (WEB-706) — the sticky right-rail card on
 * the Event v2 spine (`EventSpineTemplate`, WEB-704). Kicker, a large time
 * headline, a sub-line, the amber primary CTA with its price/availability
 * note, then a definition list of practical rows (Ages, Room, Bags, …).
 *
 * ## Where stickiness comes from
 *
 * Nowhere in this file. `EventSpineTemplate`'s `.event-spine__panel` track
 * carries `position: var(--evt-panel-pos)` (sticky ≥1024px, static below) —
 * see `eventSpine.css`. This component is a bare card that mounts INSIDE that
 * track; adding a second `position: sticky` here would double-apply the
 * offset and is deliberately not done.
 *
 * ## One of three renderings of the same payload
 *
 * The **decision section** is the ≥1024px rendering. Below that the grid
 * collapses to one column and that section relocates to a bar —
 * `EventDecisionBar` with `variant="bar"` at 768–1023 (WEB-708) and
 * `variant="bottom-bar"` at ≤767 (WEB-709). The card itself stays mounted at
 * every width so the M3 satellites (and the practical rows) keep rendering
 * (WEB-762). Which decision rendering is live is decided purely by the band
 * rules in `eventSpine.css`; all three share `decisionStyles.ts` for the CTA
 * appearance and the honest-absence strings, so the same payload can never
 * explain itself differently at a different width.
 *
 * ## Zero formatting logic
 *
 * `kicker`, `headline`, and `subline` arrive pre-formatted from the server
 * (`EventDetailV2.decisionPanel`, assembled by API-1663). This component does
 * not compute a date, a time, or a duration — it renders the strings it is
 * given, or an honest degraded state when they are absent.
 *
 * ## The two honest-absence cases (never fabricated data)
 *
 * 1. **`decisionPanel` is `undefined`** — the server could not assemble an
 *    honest panel at all (API-1663). Renders the degraded header only: the
 *    caller-supplied lexicon kicker (`EventLexiconEntry.panelKicker`, e.g.
 *    "Doors"/"Gates") + the literal "Time TBA" headline. No subline (there is
 *    no server string to show), no CTA, no `<dl>`.
 * 2. **`decisionPanel.cta.href` is `null`** — no purchasable/RSVP destination
 *    is known. Renders the CTA label as a disabled, non-focusable `<div>`
 *    (never a `<button>`/`<a>`, never a dead `#` link) with an explanatory
 *    note beneath it — the server's own `cta.note` when present, otherwise an
 *    honest fallback string. Mirrors `ProviderTicketButton`'s disabled-state
 *    contract (`molecules/ProviderTicketButton`).
 *
 * A **present** `decisionPanel` with a **null `headline`** (`startTimeKnown:
 * false` — PIP-98 / API-1676; the server now falls back to the known start
 * when doors are unpublished) also renders "Time TBA" in the headline slot,
 * but keeps the real `subline` and
 * the rest of the card intact — that is a normal render, not a degraded one.
 *
 * ## Sectioned card, not a padded one (WEB-738)
 *
 * The card itself owns **no padding** — it is `padding: 0; overflow: hidden`
 * with no shadow, and its children are *sections* that each carry their own
 * padding plus a 1px `--border` bottom rule. That is what lets a satellite
 * choose to bleed the full 398px inner width edge-to-edge (the design's
 * mini-map does exactly that); a card with 16px of its own padding
 * structurally cannot express it.
 *
 * The trailing rule is suppressed by `[&>*:last-child]:border-b-0` on the
 * **card**, not by any section knowing it is last. That matters because which
 * section is last changes with the payload — the `<dl>` normally, the decision
 * section in the absent-payload and empty-`rows` states, a satellite once one
 * mounts. A structural last-child rule is correct in all of them; a per-section
 * flag would have to be re-derived at every call site.
 *
 * ## The `children` slot
 *
 * Renders **bare** — no wrapper, no padding — directly as the card's last
 * section(s), even in the degraded state, so the M3 satellites (map, venue,
 * transit) mount without editing this file. Each satellite brings its own
 * section chrome and picks full-bleed or padded for itself. Nothing fills it
 * yet — that is expected, not a bug.
 *
 * ## View + Connected
 *
 * `EventDecisionPanelView` is pure props (`decisionPanel: EventDecisionPanel |
 * undefined`) and is what Storybook + unit tests target. `EventDecisionPanel`
 * (Connected) takes the full `EventDetailV2` + the category slug, derives
 * `decisionPanel` and the lexicon fallback kicker, and forwards to the View —
 * so a host only has to pass the event it already fetched, not re-derive the
 * lexicon lookup `EventSpineTemplate` itself already performs.
 *
 * ## Client island
 *
 * `EventSpineTemplate` is a Server Component by design (see its own file
 * header) — only the pieces that genuinely need interactivity become client
 * islands. This file is one of them: the CTA anchor wires an optional
 * `onCtaActivate` click handler (an event handler prop can only be passed to
 * a Client Component), so `'use client'` is required here even though the
 * rest of the component is otherwise static markup. Precedent:
 * `ProviderTicketButton`, `EventHero`, `AtAGlanceCard` all carry the same
 * directive for the same reason.
 */
'use client';

import * as React from 'react';
import { ArrowUpRight } from 'lucide-react';

import { RESALE_CTA_LABEL } from '@loci/types';

import { cn } from '@/components/atoms/primitives/utils';
import { ResaleMarker } from '@/components/atoms/ResaleMarker';
import { getEventLexicon } from '@/config/eventLexicon';
import { normalizeMeridiem } from '@/lib/format/normalizeMeridiem';
import { resaleVerdictOf } from '@/lib/eventDetail/carriedAdapters';
import { resaleLinkHrefOf } from '@/lib/eventDetail/resale';
import {
  EVT_MICRO_LABEL_10_MONO_CLASS,
  EVT_MICRO_LABEL_DT_CLASS,
  EVT_MICRO_LABEL_NOTE_CLASS,
  EVT_MICRO_LABEL_SUBLINE_CLASS,
} from '@/lib/eventV2MicroLabel';
import type {
  EventDecisionPanel as EventDecisionPanelPayload,
  EventDetailV2,
} from '@/types/eventDetail';

import {
  DECISION_CTA_DISABLED_CLASS,
  DECISION_CTA_LINK_CLASS,
  DECISION_CTA_MIN_HEIGHT_CLASS,
  HEADLINE_FALLBACK,
  resolveDecisionCtaNote,
  shouldHideDisabledDecisionCta,
} from './decisionStyles';

/**
 * The section primitive (WEB-738). Every direct child of the card is one of
 * these: its own padding, and the 1px rule that separates it from the next.
 *
 * It carries **no padding of its own** — each section states its own, because
 * the design's are not uniform (20px for the decision section, `16px 20px` for
 * the `<dl>`, and a satellite may choose zero to bleed edge-to-edge). Exported
 * so an M3 satellite can opt into the same chrome instead of re-deriving the
 * border colour and width.
 *
 * The trailing rule is **not** suppressed here — see the card root's
 * `[&>*:last-child]:border-b-0`, which is what makes "the last section has no
 * rule" hold across every honest-absence permutation.
 */
export const PANEL_SECTION_CLASS = 'border-b border-ds-border';

export interface EventDecisionPanelViewProps {
  /**
   * The server-assembled payload (`EventDetailV2.decisionPanel`). `undefined`
   * means the server could not assemble an honest panel — see the file header
   * for the degraded-render contract.
   */
  decisionPanel: EventDecisionPanelPayload | undefined;
  /**
   * `EventLexiconEntry.panelKicker` (e.g. "Doors" / "Gates" / "Seating") —
   * shown in the degraded header when `decisionPanel` is absent, since there
   * is no server-supplied kicker string to fall back to. Callers resolve this
   * once via `getEventLexicon(categorySlug).panelKicker`; this component never
   * re-derives the lexicon itself. Ignored when `decisionPanel` is present —
   * the server's own `kicker` always wins.
   */
  fallbackKicker: string;
  /**
   * M3 satellites (map, venue, transit) mount here, below the `<dl>`, as the
   * card's own last section(s) — rendered bare, with no wrapper and no
   * padding, so a satellite can bleed the full inner width edge-to-edge or
   * pad itself, its choice (WEB-738). Renders unconditionally, including in
   * the degraded state, so those modules land without this file changing.
   * A satellite that wants the design's section chrome brings
   * {@link PANEL_SECTION_CLASS}'s treatment itself; the card suppresses the
   * bottom rule on whichever section ends up last.
   */
  children?: React.ReactNode;
  /** Fired once when the user activates a usable (non-disabled) CTA link. */
  onCtaActivate?: () => void;
  /**
   * WEB-1098 — `true` for a confirmed Ticketmaster resale listing: the DS
   * 1.10.0 `.a-resale` marker renders right of the kicker. Unknown or primary
   * renders nothing.
   */
  resaleListing?: boolean;
  /**
   * WEB-1098 — destination of the secondary "Resale on Ticketmaster" link
   * (`.m-resale-link`), already filtered by `resaleLinkHrefOf`. `null`/omitted
   * renders no section.
   */
  resaleLinkHref?: string | null;
  /** Additional class names on the root card. */
  className?: string;
}

/**
 * `EventDecisionPanelView` — pure-props presentational View. Storybook and
 * unit tests target this directly; see the file header for the full render
 * contract.
 */
export function EventDecisionPanelView({
  decisionPanel,
  fallbackKicker,
  children,
  onCtaActivate,
  resaleListing = false,
  resaleLinkHref = null,
  className,
}: EventDecisionPanelViewProps): React.ReactElement {
  const kickerText = decisionPanel ? decisionPanel.kicker : fallbackKicker;
  const headlineText = normalizeMeridiem(decisionPanel?.headline ?? HEADLINE_FALLBACK);
  // Absent payload: there is no server subline string at all — omitting it is
  // the honest choice, never a fabricated "day, date" guess.
  const sublineText = decisionPanel ? decisionPanel.subline : null;

  const cta = decisionPanel?.cta ?? null;
  const hasRows = decisionPanel != null && decisionPanel.rows.length > 0;

  // Shared with the tablet/mobile bars so the same payload never explains
  // itself differently at a different width — see `decisionStyles.ts`.
  const note = resolveDecisionCtaNote(cta);

  return (
    <div
      data-testid="event-decision-panel"
      className={cn(
        // No padding, no gap, no shadow: the card is a shadowless frame and its
        // SECTIONS own the padding + the separating rules (WEB-738). `overflow:
        // hidden` is what clips a full-bleed satellite to the 14px radius
        // instead of letting it square off the card's corners.
        'flex w-full flex-col overflow-hidden rounded-ds-lg border border-ds-border',
        'bg-ds-surface text-fg-1',
        // Whichever section renders last carries no trailing rule — which one
        // that is changes with the payload (the `<dl>`, the decision section
        // when rows are empty or the payload is absent, a satellite once one
        // mounts), so this is stated structurally rather than re-derived per
        // state. Beats each section's own `border-b` on specificity.
        '[&>*:last-child]:border-b-0',
        className,
      )}
    >
      {/* Decision section — kicker (mono caps) → headline (26px display) →
          subline (mono caps) → CTA → note, at the design's GRADUATED 10/3/18/8
          rhythm rather than a uniform gap. The subline is nearly locked to the
          headline (3px) so the two read as one time statement; the 18px before
          the CTA is what separates "when" from "act". Stated as `margin-top` on
          each successive element, not a flex `gap`, precisely because the four
          values differ — and because a margin on the CTA block holds at 18px
          whether or not the subline above it rendered.

          Plain <div>, not <header>/<section>: the HTML-AAM `role="banner"`
          demotion only applies when a <header> is nested under
          article/aside/main/nav/section. This component's own root is a bare
          <div>, so a real <header> here would resolve to role="banner" wherever
          it mounts standalone (e.g. the M3 tablet/mobile decision bars) —
          landmark mapping would depend on the parent DOM at mount time instead
          of being fixed by this file. Safe inside the spine's <aside>, but not
          guaranteed at every call site. */}
      <div
        className={cn(PANEL_SECTION_CLASS, 'flex flex-col p-5')}
        data-testid="event-decision-panel-decision"
      >
        <div className="flex flex-col" data-testid="event-decision-panel-head">
          {/* WEB-1098 — the kicker row carries the resale marker on its right
              (DS 1.10.0 `.dec__head`: `DOORS ··· [Resale]`). */}
          <div
            className="flex items-center justify-between gap-3"
            data-testid="event-decision-panel-kicker-row"
          >
            <p
              data-testid="event-decision-panel-kicker"
              className={cn(EVT_MICRO_LABEL_10_MONO_CLASS, 'text-fg-3')}
            >
              {kickerText}
            </p>
            {resaleListing ? <ResaleMarker /> : null}
          </div>
          <p
            data-testid="event-decision-panel-headline"
            // 26px matches the spine's own `--evt-sec` (section-heading) tier —
            // the same size the design calls out. The literal fallback keeps this
            // card legible when rendered outside `.event-spine` (its own isolated
            // Storybook stories), where the custom property is not defined.
            className="mt-[10px] font-display text-[length:var(--evt-sec,26px)] font-bold leading-[1.10] text-fg-1"
          >
            {headlineText}
          </p>
          {sublineText ? (
            <p
              data-testid="event-decision-panel-subline"
              className={cn(EVT_MICRO_LABEL_SUBLINE_CLASS, 'mt-[3px] text-fg-2')}
            >
              {sublineText}
            </p>
          ) : null}
        </div>

        {/* CTA — only when a real payload exists. Absent payload => no CTA at all. */}
        {cta ? (
          <div className="mt-[18px] flex flex-col" data-testid="event-decision-panel-cta-block">
            {cta.href !== null ? (
              <a
                href={cta.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-decision-panel-cta"
                data-state="enabled"
                data-cta-state="active"
                onClick={onCtaActivate}
                // Shared appearance (colour, type, hover/active/focus-visible)
                // lives in `decisionStyles.ts` so the bar CTAs cannot drift from
                // this one; only the panel's own full-width sizing is local.
                className={cn(
                  DECISION_CTA_LINK_CLASS,
                  DECISION_CTA_MIN_HEIGHT_CLASS,
                  'w-full px-4 text-body',
                )}
              >
                <span>{cta.label}</span>
                <ArrowUpRight
                  aria-hidden="true"
                  className={cn(
                    'size-[15px] shrink-0 transition-transform duration-standard',
                    'group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transition-none',
                  )}
                />
              </a>
            ) : shouldHideDisabledDecisionCta(cta) ? null : (
              // Honest disabled state (WEB-706): the label renders inert — never a
              // dead `#` link. A bare, tabindex-less <div> is not part of the tab
              // order (no href, no button role, no explicit tabIndex). A missing
              // link with no server note hides this control (WEB-1190 D6).
              <div
                data-testid="event-decision-panel-cta"
                aria-disabled="true"
                data-state="disabled"
                data-cta-state="disabled"
                className={cn(
                  DECISION_CTA_DISABLED_CLASS,
                  DECISION_CTA_MIN_HEIGHT_CLASS,
                  'w-full px-4 text-body',
                )}
              >
                <span>{cta.label}</span>
              </div>
            )}

            {note ? (
              <p
                data-testid="event-decision-panel-cta-note"
                className={cn(EVT_MICRO_LABEL_NOTE_CLASS, 'mt-[8px] text-center text-fg-3')}
              >
                {note}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* WEB-1098 — the secondary "Resale on Ticketmaster" link (DS 1.10.0
          `.m-resale-link`, DES-98 §2): its OWN section, directly under the
          decision section and above the <dl>. Because the ≤1023 band rule hides
          only the decision section (WEB-762), this section stays mounted at
          every width and never enters either bar, so WEB-709's one-decision
          contract holds. A text link, never a button: the amber CTA is the one
          decision. Its accessible name says it opens a new tab. */}
      {resaleLinkHref ? (
        <div
          className={cn(PANEL_SECTION_CLASS, 'm-resale-link')}
          data-testid="event-decision-panel-resale-link"
        >
          <a
            href={resaleLinkHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${RESALE_CTA_LABEL} (opens in a new tab)`}
            className="m-resale-link__a a-hit-floor"
            data-testid="event-decision-panel-resale-link-anchor"
          >
            {RESALE_CTA_LABEL}
            <ArrowUpRight aria-hidden="true" />
          </a>
          <span className="m-resale-link__note">Prices set by sellers</span>
        </div>
      ) : null}

      {/* Practical rows — never an empty <dl>; omitted entirely when rows is [].
          The `<dl>` IS its own section (WEB-738): it carries the section chrome
          and the design's `16px 20px` padding directly. WEB-740: each row is
          `grid / auto 1fr / 14px`, not flex+space-between, so a long value wraps
          inside a stable column instead of shoving the key. `min-w-0` on <dd> is
          load-bearing — grid items default to `min-width: auto` and would
          otherwise refuse to shrink below the value's max-content width. <dt>
          type is WEB-736's shared micro-label (`EVT_MICRO_LABEL_DT_CLASS`); this
          issue does not set a second literal. */}
      {hasRows ? (
        <dl
          data-testid="event-decision-panel-rows"
          className={cn(PANEL_SECTION_CLASS, 'flex flex-col gap-[11px] px-5 py-4')}
        >
          {decisionPanel!.rows.map((row, index) => (
            <div
              // Row labels are practical facts (Ages, Room, Bags…), not a stable
              // id off the wire — index disambiguates a duplicate key safely.
              key={`${row.key}-${index}`}
              className="grid min-w-0 grid-cols-[auto_1fr] items-baseline gap-[14px]"
              data-testid="event-decision-panel-row"
            >
              <dt
                data-testid="event-decision-panel-row-key"
                className={cn(EVT_MICRO_LABEL_DT_CLASS, 'text-fg-3')}
              >
                {row.key}
              </dt>
              <dd
                data-testid="event-decision-panel-row-value"
                className="min-w-0 text-right text-small font-normal leading-[1.45] wrap-break-word text-fg-2"
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* M3 satellite slot — map / venue / transit mount here without editing
          this file. Rendered BARE: no wrapper, no padding, so a satellite is
          itself a direct section of the card and can bleed the full 398px inner
          width edge-to-edge (the design's mini-map does). A wrapper would both
          re-impose a container a full-bleed child cannot escape and, rendered
          unconditionally, permanently occupy `:last-child` — leaving the `<dl>`
          above it with a trailing rule flush against the card's own bottom
          border in every state where no satellite is mounted. Bare `{children}`
          contributes nothing to the DOM when empty, so the last REAL section is
          always the one the card's `:last-child` rule finds. */}
      {children}
    </div>
  );
}

export interface EventDecisionPanelProps {
  /** Event detail payload from `fetchEventDetail` (WEB-197, API-1404). */
  event: EventDetailV2;
  /**
   * Category slug driving the degraded-state lexicon kicker. Same slug
   * `EventSpineTemplate` resolves its own lexicon from — pass the same value
   * so the panel's fallback kicker never disagrees with the rest of the page.
   * Anything outside the canonical ten (including `null`/omitted) resolves to
   * `GENERIC_EVENT_LEXICON`, which is a normal outcome, not an error.
   */
  categorySlug?: string | null;
  children?: React.ReactNode;
  onCtaActivate?: () => void;
  className?: string;
}

/**
 * `EventDecisionPanel` — Connected. Derives `decisionPanel` and the lexicon
 * fallback kicker from the already-fetched `EventDetailV2`, so a host only
 * passes the event and the category slug rather than re-deriving the lexicon
 * lookup `EventSpineTemplate` itself already performs. No RTK Query hook: the
 * payload rides on the event detail response the route already fetched
 * (API-1661/API-1663), so there is nothing further to fetch here.
 */
export function EventDecisionPanel({
  event,
  categorySlug,
  children,
  onCtaActivate,
  className,
}: EventDecisionPanelProps): React.ReactElement {
  const slug = typeof categorySlug === 'string' ? categorySlug.trim() : '';
  const lexicon = getEventLexicon(slug);

  return (
    <EventDecisionPanelView
      decisionPanel={event.decisionPanel}
      fallbackKicker={lexicon.panelKicker}
      onCtaActivate={onCtaActivate}
      // WEB-1098 — a marker only on a POSITIVE verdict: an unknown payload is
      // not evidence of resale.
      resaleListing={resaleVerdictOf(event) === true}
      resaleLinkHref={resaleLinkHrefOf(event)}
      className={className}
    >
      {children}
    </EventDecisionPanelView>
  );
}
